import { Accordion, Grid, Heading } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { useFulfillmentCapabilities } from 'hooks/store-fulfillment/useFulfillmentCapabilities'
import { useEssentials } from 'hooks/utils/useEssentials'
import './capabilities-store-level.scss'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import {
    CapabilityRow,
    formatLocalWithDateFns,
} from 'pages/fulfillment/helpers'

export const StoreFulfillmentCapabilitiesStore = () => {
    const { env, session, currentLocation, t } = useEssentials()
    const storeFulfillment = TRANSLATION_KEYS.storeFulfillment
    const { headings } = storeFulfillment
    const { store } = storeFulfillment.inf
    const capabilityHeadingKeys: Record<string, string> = {
        pickup: headings.pickup,
        'grocery pickup': headings.groceryPickup,
        'drive up': headings.driveUp,
        'drive up returns': headings.driveUpReturns,
        'drive up starbucks': headings.driveUpStarbucks,
        'parking lot delivery pause': headings.parkingLotDeliveryPause,
        sfs: headings.sfs,
        ndd: headings.ndd,
        sdd: headings.sdd,
    }

    const {
        data,
        calledAt,
        isCalledAtToday,
        isLoading,
        isError,
        error,
        refetch,
    } = useFulfillmentCapabilities({
        env,
        accessToken: session?.accessToken ?? '',
        locationId: currentLocation,
    })
    const rows = data?.rows ?? []
    const time = isCalledAtToday
        ? `${t(storeFulfillment.timePeriods.today)} ${t(store.asOf)} ${calledAt}`
        : `${t(store.asOf)} ${calledAt}`

    if (isLoading) {
        return (
            <div className="placeholder-container">
                <BasicPlaceholder type="text" rows={1} columns={1} />
                <BasicPlaceholder type="table" rows={10} columns={1} />
            </div>
        )
    }

    if (isError) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    if (!isError && rows.length === 0) {
        return <ErrorAlert variant="no-data" />
    }

    return (
        <Grid.Container className="hc8-pt-4x">
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <p className="hc8-fs-050 hc8-ma-none">{time}</p>
            </Grid.Item>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <Table
                    alternateRowColor={true}
                    cellPadding={'normal'}
                    className="capabilities-table"
                >
                    <>
                        <tr>
                            <td>{t(store.capabilityStatus)}</td>
                        </tr>
                    </>
                    <>
                        {rows?.map((row: CapabilityRow) => {
                            const capability = row?.[0]?.value ?? ''
                            const capabilityTranslationKey =
                                capabilityHeadingKeys[capability.toLowerCase()]
                            const capabilityLabel = capabilityTranslationKey
                                ? t(capabilityTranslationKey)
                                : capability
                            const statusRaw = String(
                                row?.[1]?.value ?? '',
                            ).toLowerCase()
                            const reason = row?.[2]?.value ?? ''
                            const lastUpdated = row?.[3]?.value ?? ''

                            const isTrue = statusRaw === 'true'
                            const parkingLotDeliveryPause =
                                capability === 'parking lot delivery pause'
                            const isPaused =
                                statusRaw === 'false' && parkingLotDeliveryPause

                            return (
                                <tr key={capability}>
                                    <td>
                                        {isTrue ? (
                                            <div className="cap-row">
                                                <div>{capabilityLabel}</div>

                                                <div className="cap-row__right">
                                                    {!parkingLotDeliveryPause && (
                                                        <div>{t(store.ON)}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <Accordion
                                                spacing={'dense'}
                                                headerSlot={
                                                    <div className="cap-row">
                                                        <Heading
                                                            size={10}
                                                            className="cap-heading hc8-ma-none"
                                                        >
                                                            {capabilityLabel}
                                                        </Heading>

                                                        <span className="cap-row__right">
                                                            {isPaused
                                                                ? t(
                                                                      store.PAUSED,
                                                                  )
                                                                : t(store.OFF)}
                                                        </span>
                                                    </div>
                                                }
                                            >
                                                <div className="cap-expand-content hc8-pv-3x hc8-ph-4x">
                                                    {lastUpdated && (
                                                        <div className="cap-expand-block hc8-mb-2x">
                                                            {t(
                                                                store.lastUpdated,
                                                            )}
                                                            {': '}
                                                            {formatLocalWithDateFns(
                                                                lastUpdated,
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="cap-expand-block--reason">
                                                        {t(store.reason)}
                                                        {': '}
                                                        {reason}
                                                    </div>
                                                </div>
                                            </Accordion>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </>
                </Table>
            </Grid.Item>
        </Grid.Container>
    )
}
